import "./TrialPage.scss";

import { Alert, Flex, Loader, Text } from "@fluentui/react-northstar";
import { TeamsSubscriptionPlans } from "@teams/types";
import { UserDTO } from "@teams/types/api";
import React, { useCallback, useEffect, useState } from "react";

import { useDatadog } from "../../_shared/actions/useDatadog";
import { Congratulations } from "../../_shared/components/Checkout/Congratulations";
import { SubscriptionStateDisplay } from "../../_shared/components/NavBar/SubscriptionStateDisplay";
import { TeamsLoader } from "../../_shared/components/TeamsLoader";
import { isError, isSuccess } from "../../_shared/services/ChannelApi";
import { userApi } from "../../_shared/services/UserApi";
import { getPageContext } from "../../_shared/utils/teamsHelpers";
import { PageHeader } from "../PageHeader";
import { useMicrosoftAuth } from "../useMicrosoftAuth";

export const TrialPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const pageContext = getPageContext();
  const [authError, setAuthError] = useState<string>();
  const { result } = useMicrosoftAuth(["openid", "profile", "email"]);
  const [user, setUser] = useState<UserDTO>();
  const { initialize: dataDogInit, log } = useDatadog("teams-trial-page", 100, 0);

  const plan: TeamsSubscriptionPlans =
    pageContext === "/msteams" ? "msteams-standard" : "agile-standup";

  useEffect(() => {
    if (!result?.idToken) {
      return;
    }

    (async () => {
      const u = await userApi.getUserWithToken(result.idToken);
      if (isSuccess(u)) {
        setUser(u);
        dataDogInit(u.userId);
      } else {
        log({ logLevel: "error", message: "Failed to get user: " + u.message });
        setAuthError(u.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.idToken]);

  const startTrialForUser = useCallback(async () => {
    if (!result?.idToken || !user) {
      return;
    }
    const trialUser = await userApi.startTrialByToken(result?.idToken, "na", plan, user?.upn);
    if (isError(trialUser)) {
      console.error(trialUser);
      setErrorMessage("There was a problem starting your trial.  Contact support");
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.idToken, user, plan]);

  const isActiveTrial = user?.subscriptions.find(
    subscription =>
      subscription.plan === plan && subscription.state === "active" && subscription.type === "trial"
  );
  useEffect(() => {
    if (!isActiveTrial) {
      setIsLoading(true);
      startTrialForUser().then(() => setIsLoading(false));
    }
  }, [startTrialForUser, isActiveTrial]);

  if (authError) {
    return (
      <Alert content="Oops, something went wrong please try again or contact support" warning />
    );
  }

  if (!user || !result?.idToken) {
    return <Loader />;
  }

  const info = {
    Name: user.name,
    UPN: user.upn,
    "User ID": user.userId,
    "Tenant ID": user.tenantId,
    Application: pageContext.replace("/", ""),
  };

  return (
    <section>
      <PageHeader
        helpLink={{
          url: "https://www.polly.ai/help/microsoft-teams/free-trial-faqs",
          label: "Trial FAQs",
        }}
      />
      <Flex column hAlign="center">
        {!isActiveTrial && (
          <TeamsLoader isLoading={isLoading} loadingMessage="Loading">
            <div className="trial-page__animation-container">
              <Congratulations licenseRequest plan={plan} />
            </div>
          </TeamsLoader>
        )}
        <div className="trial-page__user-info">
          <h3>User Information:</h3>
          <p>{prettyPrint(info)}</p>
        </div>
        {isActiveTrial && (
          <SubscriptionStateDisplay key={user.subscriptions[0].id} isMobile={false} />
        )}
        <Text color="red" content={errorMessage} />
      </Flex>
    </section>
  );
};

const prettyPrint = (item: { [key: string]: any }, elements: JSX.Element[] = []) => {
  for (const key in item) {
    if (typeof item[key] === "object") {
      prettyPrint(item[key], elements);
      continue;
    }
    elements.push(
      <pre>
        <strong>{key}:&nbsp;</strong>
        {item[key].toString()}
      </pre>
    );
  }
  return elements;
};
